import { Spinner } from "@blueprintjs/core";
import { ReactPlayerErrorBoundaryRetry } from "@package/ui-apollo-components";
import { EventPayloadType } from "@package/ui-gql/index-analytics";
import { useSelectorWall } from "@package/ui-redux";
import { useCallback, useEffect, useState } from "react";
import { useWallAnalytics } from "../../context/context-analytics/context-analytics";
import { useWallProvider } from "../../context/context-wall/context-wall";

function RestingMediaContent() {
  const resting = useSelectorWall((state) => state.wall.resting);
  const provider = useWallProvider();
  const { trigger } = useWallAnalytics();
  const [url, setUrl] = useState<string | undefined>();

  useEffect(
    function effectFetchMediaUrl() {
      const fetchMediaUrl = async () => {
        if (resting.active) {
          if (resting.media?.id) {
            const url = await provider?.fetchMediaUrl(resting.media.id);
            if (url) {
              setUrl(url);
              return;
            }
          }

          console.error("Resting media has no URL, completing video.");
          provider?.onRestingMediaVideoCompleted();
        }
      };

      fetchMediaUrl();

      return () => {
        setUrl(undefined);
      };
    },
    [resting.active, resting.media?.id, provider],
  );

  useEffect(
    function effectOnRestingMediaStarted() {
      if (resting.active && resting.media?.id) {
        trigger(EventPayloadType.RestingMediaStarted, {
          media: resting.media.id,
        });
      }
    },
    [resting.active, resting.media?.id, trigger],
  );

  if (resting.active) {
    if (!url) {
      return (
        <div className="w-full h-full flex flex-col items-center justify-center">
          <Spinner size={100} />
        </div>
      );
    }

    if (resting.type === "image") {
      return <img width="100%" height="100%" src={url} />;
    }

    return (
      <ReactPlayerErrorBoundaryRetry
        width="100%"
        height="100%"
        loop={false}
        muted={!resting.audio}
        playing
        pip={false}
        url={url}
        onEnded={() => {
          provider?.onRestingMediaVideoCompleted();

          if (resting.media?.id) {
            trigger(EventPayloadType.RestingMediaTimedOut, {
              media: resting.media.id,
            });
          }
        }}
        onErrorEvent={(error) => {
          provider?.onRestingMediaVideoCompleted();
          console.error("Resting media video error:", error);
        }}
        onMediaStalled={() => {
          provider?.onRestingMediaVideoCompleted();
          console.error("Resting media video stalled, completing video.");
        }}
      />
    );
  }

  return null;
}

export function RestingMedia() {
  const resting = useSelectorWall((state) => state.wall.resting);
  const provider = useWallProvider();
  const { trigger } = useWallAnalytics();
  const classNameBgColor = resting.transparent ? "bg-transparent" : "bg-black";

  const onHandleMouse = useCallback(
    (x: number, y: number) => {
      if (resting.interrupt) {
        provider?.onRestingMediaInteracted();

        if (resting.media?.id) {
          trigger(EventPayloadType.RestingMediaInteracted, {
            media: resting.media.id,
            position: {
              x,
              y,
            },
          });
        }
      }
    },
    [provider, resting.media?.id, resting.interrupt, trigger],
  );

  if (!resting.active) {
    return null;
  }

  return (
    <div
      className={`absolute left-0 top-0 h-full w-full z-40 overflow-hidden ${classNameBgColor}`}
      onPointerDown={(e) => onHandleMouse(e.screenX, e.screenY)}
    >
      <RestingMediaContent />
    </div>
  );
}
